import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Alert, Spinner } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import Particle from "../Particle";

function PasswordReset() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [tokenValid, setTokenValid] = useState(true);
  const [sessionEstablished, setSessionEstablished] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Create Supabase client once
  const supabase = createClient(
    process.env.REACT_APP_SUPABASE_URL,
    process.env.REACT_APP_SUPABASE_ANON_KEY
  );
  
  // Extract code and process it immediately
  useEffect(() => {
    const processResetCode = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        
        // Check if user arrived from app (could be indicated by a special parameter)
        const fromApp = params.get('source') === 'app' || localStorage.getItem('fromApp');
        if (fromApp) {
          // Store this for potential use later
          localStorage.setItem('fromApp', 'true');
        }
        
        if (!code) {
          setTokenValid(false);
          setMessage({
            text: "Invalid or missing reset code. Please request a new password reset link.",
            type: "danger"
          });
          return;
        }
        
        // Verify the code is valid without exchanging it yet
        // This just checks if the code is in the right format
        if (!code.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/)) {
          setTokenValid(false);
          setMessage({
            text: "Invalid reset code format. Please request a new password reset link.",
            type: "danger"
          });
          return;
        }

        // Important: Exchange the code for a session here
        // This establishes the auth session before the form is submitted
        console.log("Attempting to exchange code for session...");
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        
        if (error) {
          console.error("Code exchange error:", error);
          setTokenValid(false);
          setMessage({
            text: `Error establishing session: ${error.message}. Please request a new password reset link.`,
            type: "danger"
          });
          return;
        }
        
        console.log("Session established successfully", data);
        setSessionEstablished(true);
      } catch (error) {
        console.error("Error processing reset code:", error);
        setTokenValid(false);
        setMessage({
          text: "Error processing reset code. Please request a new password reset link.",
          type: "danger"
        });
      }
    };
    
    processResetCode();
  }, [location, supabase.auth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setMessage({ text: "Passwords do not match", type: "danger" });
      return;
    }
    
    // Validate password strength
    if (password.length < 8) {
      setMessage({ text: "Password must be at least 8 characters long", type: "danger" });
      return;
    }
    
    setLoading(true);
    setMessage({ text: "", type: "" });
    
    try {
      if (!sessionEstablished) {
        throw new Error("No active session. Please try again with a new reset link.");
      }
      
      // Now that we have an established session, we can update the password
      console.log("Updating password...");
      const { error } = await supabase.auth.updateUser({ 
        password: password 
      });
      
      if (error) {
        console.error("Password update error:", error);
        throw new Error(error.message);
      }
      
      console.log("Password updated successfully");
      setMessage({ 
        text: "Password reset successful! You can now log in with your new password.", 
        type: "success" 
      });
      
      // Check if user came from app to customize the message
      const fromApp = localStorage.getItem('fromApp') === 'true';
      if (fromApp) {
        setMessage({ 
          text: "Password reset successful! You can now return to the NuroFit app and log in with your new password.", 
          type: "success" 
        });
      }
      
      // Redirect to a success page after a delay
      setTimeout(() => {
        navigate("/nurofit/reset-success");
        // Clear the fromApp flag
        localStorage.removeItem('fromApp');
      }, 3000);
    } catch (error) {
      console.error("Password reset error:", error);
      setMessage({ 
        text: "Failed to reset password: " + error.message, 
        type: "danger" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="about-section position-relative" style={{ background: "linear-gradient(180deg, #121212 0%, #1a1a1a 100%)" }}>
      <div style={{ position: "absolute", width: "100%", height: "100%", top: 0, left: 0 }}>
        <Particle />
      </div>
      <Container style={{ paddingTop: "120px", paddingBottom: "120px", position: "relative", zIndex: 5 }}>
        <Row className="justify-content-center">
          <Col md={8} lg={6} xl={5}>
            <div 
              style={{
                background: "rgba(255, 255, 255, 0.05)",
                borderRadius: "20px",
                padding: "40px",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
                position: "relative",
              }}
            >
              <h1 
                style={{
                  fontSize: "2.2em",
                  fontWeight: "700",
                  textAlign: "center",
                  marginBottom: "30px",
                  background: "linear-gradient(90deg, #805AD5 0%, #D53F8C 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Reset Your Password
              </h1>
              
              {message.text && (
                <Alert variant={message.type} className="mb-4">
                  {message.text}
                </Alert>
              )}
              
              {tokenValid && (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ color: "#e0e0e0" }}>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      style={{
                        background: "rgba(255, 255, 255, 0.1)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        color: "#ffffff",
                        padding: "12px 15px",
                        borderRadius: "10px",
                        position: "relative",
                      }}
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label style={{ color: "#e0e0e0" }}>Confirm Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      style={{
                        background: "rgba(255, 255, 255, 0.1)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        color: "#ffffff",
                        padding: "12px 15px",
                        borderRadius: "10px",
                        position: "relative",
                      }}
                    />
                  </Form.Group>
                  
                  <Button
                    type="submit"
                    disabled={loading || !sessionEstablished}
                    style={{
                      width: "100%",
                      background: "linear-gradient(90deg, #805AD5 0%, #D53F8C 100%)",
                      border: "none",
                      padding: "12px",
                      borderRadius: "10px",
                      fontSize: "1.1em",
                      fontWeight: "600",
                      marginTop: "15px",
                      position: "relative",
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Resetting...
                      </>
                    ) : !sessionEstablished ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Verifying...
                      </>
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </Form>
              )}
              
              <div className="mt-4 text-center">
                <Button
                  variant="link"
                  onClick={() => navigate("/nurofit")}
                  style={{
                    color: "#b0b0b0",
                    textDecoration: "none",
                    position: "relative",
                  }}
                >
                  Return to NuroFit
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default PasswordReset;